import * as Dialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';

/**
 * The area's side panel (the approved boards): from the right on a wide screen, a sheet from the
 * bottom on a phone, with its one primary action in the footer, within reach of the thumb. It is a
 * modal dialog, portalled into the shell so its phone layout follows the `shell` container query
 * in shell.css like everything else.
 */
export function SidePanel({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  onCloseAutoFocus,
}: {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly title: string;
  readonly description?: string;
  readonly children: ReactNode;
  readonly footer: ReactNode;
  readonly onCloseAutoFocus?: (event: Event) => void;
}) {
  const { t } = useTranslation('administration');
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal container={document.querySelector<HTMLElement>('.app-shell') ?? undefined}>
        <Dialog.Overlay className="place-picker-veil" />
        <Dialog.Content
          className="side-panel"
          onCloseAutoFocus={onCloseAutoFocus}
          {...(description === undefined ? { 'aria-describedby': undefined } : {})}
        >
          <span aria-hidden="true" className="place-picker-handle" />
          <div className="side-panel-head">
            <div className="min-w-0">
              <Dialog.Title className="text-foreground text-xl leading-tight font-bold">
                {title}
              </Dialog.Title>
              {description !== undefined ? (
                <Dialog.Description className="text-muted-foreground mt-1 text-[13.5px]">
                  {description}
                </Dialog.Description>
              ) : null}
            </div>
            <Dialog.Close asChild>
              <Button variant="ghost" size="icon" aria-label={t('common.close')}>
                <X />
              </Button>
            </Dialog.Close>
          </div>
          <div className="side-panel-body">{children}</div>
          <div className="side-panel-foot">{footer}</div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
